import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button, LinearProgress } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CallOutlinedIcon from "@material-ui/icons/CallOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";
import ChatOutlinedIcon from "@material-ui/icons/ChatOutlined";
import DirectionsWalkOutlinedIcon from "@material-ui/icons/DirectionsWalkOutlined";
import PeopleOutlinedIcon from "@material-ui/icons/PeopleOutlined";
import EventNoteOutlinedIcon from "@material-ui/icons/EventNoteOutlined";
import HistoryOutlinedIcon from "@material-ui/icons/HistoryOutlined";
import PostAddOutlinedIcon from "@material-ui/icons/PostAddOutlined";
import { showAppConfirm } from "../../../context/AlertContext";
import useStyles from "./styles";
import CrmService, {
  apiError,
  childAge,
  fmtDate,
  fmtDateTime,
  relativeDue,
  studentFormPath,
  studentRemovalWarning,
  telHref,
  whatsappHref,
} from "./crmService";
import { EmptyState, StatusDot, useCrmSettings, useToast } from "./crmUi";
import FollowUpDialog from "./FollowUpDialog";
import EnquiryFormDrawer from "./EnquiryFormDrawer";

const TYPE_ICONS = {
  Call: CallOutlinedIcon,
  "WhatsApp/SMS": ChatOutlinedIcon,
  Email: EmailOutlinedIcon,
  "School Tour/Visit": DirectionsWalkOutlinedIcon,
  Meeting: PeopleOutlinedIcon,
  Other: EventNoteOutlinedIcon,
};

const ORIGIN_LABEL = { manual: "added in the CRM", quick: "Quick add", import: "imported", "online-form": "online enquiry form" };

function Fact({ label, children }) {
  const classes = useStyles();
  if (children == null || children === "" || children === false) return null;
  return (
    <>
      <dt className={classes.factKey}>{label}</dt>
      <dd className={classes.factVal}>{children}</dd>
    </>
  );
}

/** One enquiry: summary, contact buttons, follow-up timeline (newest first). */
export default function CrmEnquiryDetail() {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const { tz } = useCrmSettings();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [logOpen, setLogOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [toast, toastNode] = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await CrmService.get(id));
    } catch (err) {
      setError(err?.response?.status === 404 ? "This enquiry no longer exists." : apiError(err, "Could not load the enquiry.").message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const back = (
    <button type="button" className={classes.back} onClick={() => history.push("/app/crm/enquiries")}>
      <ArrowBackIcon style={{ fontSize: 16 }} /> Enquiries
    </button>
  );

  if (!data) {
    return (
      <div className={classes.page}>
        {back}
        {loading ? (
          <LinearProgress />
        ) : (
          <div className={classes.panel}>
            <EmptyState
              title="Could not open the enquiry"
              text={error}
              action={
                <Button variant="contained" color="primary" onClick={load}>
                  Retry
                </Button>
              }
            />
          </div>
        )}
      </div>
    );
  }

  const e = data.enquiry;
  const followUps = data.followUps || [];
  const age = childAge(e);
  const remove = async () => {
    const ok = await showAppConfirm(`Delete enquiry ${e.enquiryNo} (${e.parentName || e.childName})? Its follow-up history is deleted too.${studentRemovalWarning(e)}`);
    if (!ok) return;
    try {
      await CrmService.remove(e._id);
      history.push("/app/crm/enquiries");
    } catch (err) {
      toast(apiError(err, "Could not delete.").message, "error");
    }
  };

  return (
    <div className={classes.page}>
      {back}
      {loading && <LinearProgress style={{ marginBottom: 8 }} />}
      <div className={classes.head}>
        <div>
          <div className={classes.muted}>
            {e.enquiryNo} · enquired {fmtDate(e.enquiryDate, tz)}
          </div>
          <h1 className={classes.heroName}>{e.parentName || e.childName || "Enquiry"}</h1>
          <div className={classes.heroRow}>
            <StatusDot status={e.status} />
            {e.status === "Lost" && e.lostReason && <span className={classes.muted}>Reason: {e.lostReason}</span>}
            {e.dueState && (
              <span
                className={classes.due}
                style={{ color: e.dueState === "overdue" ? "#b3261e" : e.dueState === "today" ? "#8a5600" : "#1a1a1a", fontSize: 14 }}
              >
                Next: {relativeDue(e.nextFollowUpDate, tz)}
              </span>
            )}
            {e.isStale && <span className={classes.staleTag}>No follow-up for {e.daysSinceFollowUp} days</span>}
          </div>
        </div>
        <div className={classes.headActions}>
          {e.studentId ? (
            <Button variant="outlined" onClick={() => history.push(`/app/studentregistration/${e.studentId}`)}>
              View student
            </Button>
          ) : (
            e.status === "Admitted" && (
              <Button variant="outlined" onClick={() => history.push(studentFormPath(e._id))}>
                Register as student
              </Button>
            )
          )}
          <Button variant="outlined" onClick={() => setEditOpen(true)}>
            Edit
          </Button>
          <Button variant="contained" color="primary" startIcon={<PostAddOutlinedIcon />} onClick={() => setLogOpen(true)}>
            Add follow-up
          </Button>
        </div>
      </div>

      <div className={classes.detailGrid}>
        <div className={classes.stack}>
          <section className={classes.panel}>
            <h2 className={classes.panelTitle}>Contact</h2>
            <div className={classes.contactRow}>
              <Button variant="outlined" component="a" href={telHref(e.mobile)} startIcon={<CallOutlinedIcon />}>
                {e.mobile}
              </Button>
              <Button
                variant="outlined"
                component="a"
                href={whatsappHref(e.mobile, e.parentName ? `Hello ${e.parentName}, ` : "")}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsAppIcon />}
              >
                WhatsApp
              </Button>
              {e.email && (
                <Button variant="outlined" component="a" href={`mailto:${e.email}`} startIcon={<EmailOutlinedIcon />}>
                  Email
                </Button>
              )}
            </div>
            <dl className={classes.facts} style={{ marginTop: 18 }}>
              <Fact label="Relationship">{e.relationship}</Fact>
              <Fact label="Alternate phone">{e.alternatePhone && <a href={telHref(e.alternatePhone)}>{e.alternatePhone}</a>}</Fact>
              <Fact label="Email">{e.email}</Fact>
              <Fact label="Area">{e.area || <span style={{ color: "#b3261e" }}>Not added yet</span>}</Fact>
              <Fact label="Address">{e.address}</Fact>
            </dl>
          </section>

          <section className={classes.panel}>
            <h2 className={classes.panelTitle}>Child and admission</h2>
            <dl className={classes.facts} style={{ marginTop: 14 }}>
              <Fact label="Child">{e.childName || "-"}</Fact>
              <Fact label="Date of birth">{e.childDob && `${fmtDate(e.childDob, tz)}${age ? ` (${age})` : ""}`}</Fact>
              <Fact label="Age">{!e.childDob && age}</Fact>
              <Fact label="Gender">{e.gender}</Fact>
              <Fact label="Mother's name">{e.motherName}</Fact>
              <Fact label="Father's name">{e.fatherName}</Fact>
              <Fact label="Class">{e.program || "-"}</Fact>
              <Fact label="Enrollment date">{e.status === "Admitted" && e.closedAt && fmtDate(e.closedAt, tz)}</Fact>
              <Fact label="Academic year">{e.academicYear}</Fact>
              <Fact label="Current school">{e.currentSchool}</Fact>
            </dl>
          </section>

          {e.onlineForm && (
            <section className={classes.panel}>
              <h2 className={classes.panelTitle}>Online form answers</h2>
              <div className={classes.muted} style={{ marginTop: 4 }}>
                As the parent submitted them on the public enquiry form.
              </div>
              <dl className={classes.facts} style={{ marginTop: 14 }}>
                <Fact label="Father">
                  {[e.onlineForm.fatherName, e.onlineForm.fatherContactNumber].filter(Boolean).join(" · ")}
                </Fact>
                <Fact label="Mother">
                  {[e.onlineForm.motherName, e.onlineForm.motherContactNumber].filter(Boolean).join(" · ")}
                </Fact>
                <Fact label="Class / age group">{e.onlineForm.classOrAgeGroup}</Fact>
                <Fact label="Admission sought for">{e.onlineForm.admissionSoughtFor}</Fact>
                <Fact label="Additional information">
                  {e.onlineForm.additionalInformation && (
                    <span style={{ whiteSpace: "pre-wrap" }}>{e.onlineForm.additionalInformation}</span>
                  )}
                </Fact>
                {(e.customFields || []).map((f) => (
                  <Fact key={f.key} label={f.label || "Custom field"}>
                    {f.value || "-"}
                  </Fact>
                ))}
              </dl>
            </section>
          )}

          <section className={classes.panel}>
            <h2 className={classes.panelTitle}>Source and owner</h2>
            <dl className={classes.facts} style={{ marginTop: 14 }}>
              <Fact label="Source">{e.source || "-"}</Fact>
              <Fact label="Details">{e.sourceOther}</Fact>
              <Fact label="Referred by">{e.referrerName}</Fact>
              <Fact label="Sibling">{e.siblingStudentName}</Fact>
              <Fact label="Assigned to">{e.assignedTo || "Not assigned"}</Fact>
              <Fact label="Added by">{`${e.createdBy?.name || "-"} (${ORIGIN_LABEL[e.origin] || e.origin})`}</Fact>
              <Fact label="Notes">{e.notes && <span style={{ whiteSpace: "pre-wrap" }}>{e.notes}</span>}</Fact>
            </dl>
            <Button variant="text" style={{ color: "#b3261e", marginTop: 12, padding: 0 }} onClick={remove}>
              Delete enquiry
            </Button>
          </section>
        </div>

        <section className={classes.panel}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <h2 className={classes.panelTitle}>Follow-up history</h2>
            <span className={classes.muted}>{followUps.filter((f) => f.kind === "followup").length} follow-ups</span>
          </div>
          <ol className={classes.timeline}>
            {followUps.map((f) => {
              const Icon = f.kind === "status" ? HistoryOutlinedIcon : TYPE_ICONS[f.type] || EventNoteOutlinedIcon;
              const changed = f.statusAfter && f.statusAfter !== f.statusBefore;
              return (
                <li key={f._id} className={classes.tlItem}>
                  <span className={classes.tlIcon} aria-hidden>
                    <Icon style={{ fontSize: 18 }} />
                  </span>
                  <div className={classes.tlHead}>
                    <span className={classes.tlTitle}>
                      {f.kind === "status" ? "Status changed" : f.type}
                      {f.outcome ? ` · ${f.outcome}` : ""}
                    </span>
                    <span className={classes.tlWhen}>{fmtDate(f.at, tz)}</span>
                  </div>
                  {f.notes && <div className={classes.tlBody}>{f.notes}</div>}
                  <div className={classes.tlMeta}>
                    {changed && (
                      <>
                        {f.statusBefore} → <strong style={{ fontWeight: 600, color: "#1a1a1a" }}>{f.statusAfter}</strong>
                        {f.lostReason ? ` (${f.lostReason})` : ""}
                        {" · "}
                      </>
                    )}
                    {f.nextFollowUpDate && <>Next: {fmtDateTime(f.nextFollowUpDate, tz)} · </>}
                    by {f.staffName || f.doneBy?.name || "-"}
                  </div>
                </li>
              );
            })}
            <li className={classes.tlItem}>
              <span className={classes.tlIcon} aria-hidden>
                <PostAddOutlinedIcon style={{ fontSize: 18 }} />
              </span>
              <div className={classes.tlHead}>
                <span className={classes.tlTitle}>Enquiry received</span>
                <span className={classes.tlWhen}>{fmtDate(e.enquiryDate, tz)}</span>
              </div>
              <div className={classes.tlMeta}>
                {e.source ? `${e.source} · ` : ""}
                {ORIGIN_LABEL[e.origin] || e.origin}
              </div>
            </li>
          </ol>
          {followUps.length === 0 && (
            <div style={{ marginTop: 16 }}>
              <Button variant="outlined" onClick={() => setLogOpen(true)}>
                Log the first follow-up
              </Button>
            </div>
          )}
        </section>
      </div>

      <FollowUpDialog
        open={logOpen}
        enquiry={e}
        onClose={() => setLogOpen(false)}
        onSaved={() => {
          setLogOpen(false);
          toast("Follow-up logged.");
          load();
        }}
      />
      <EnquiryFormDrawer
        open={editOpen}
        enquiry={e}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          toast("Enquiry saved.");
          load();
        }}
      />
      {toastNode}
    </div>
  );
}
